import { useEffect, useRef, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  Popover,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { getLearningCourseQueryKey, submitLearningQuizMutation } from '@drivingdoc/api-client/generated/react-query';
import type { LearningQuiz, QuizResult } from '@drivingdoc/api-client/generated/types';

import QuizMinTimeCountdown from './QuizMinTimeCountdown';

const SUBMIT_TIME_TOOLTIP =
  '10 minutes must be spent on this quiz. Review your work until the timer elapses, then you may submit.';

export default function QuizRunner({
  courseId,
  quiz,
  pathLabel,
  result,
  timeSpentSeconds = 0,
  bypassMinTime = false,
}: {
  courseId: string;
  quiz: LearningQuiz;
  pathLabel?: string;
  result?: QuizResult;
  timeSpentSeconds?: number;
  bypassMinTime?: boolean;
}) {
  const client = useQueryClient();
  const submit = useMutation(submitLearningQuizMutation());
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [scoreOpen, setScoreOpen] = useState(false);
  const submitButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => setAnswers({}), [quiz.id]);

  const submitQuiz = async () => {
    await submit.mutateAsync({
      path: { courseId, itemId: quiz.id },
      body: {
        answers: quiz.questions.map((question) => ({
          questionId: question.id,
          optionId: answers[question.id],
        })),
      },
    });
    await client.invalidateQueries({ queryKey: getLearningCourseQueryKey({ path: { courseId } }) });
    setScoreOpen(true);
  };

  const complete = quiz.questions.every((question) => Boolean(answers[question.id]));
  const effectiveMinSeconds = bypassMinTime ? 0 : quiz.minTimeSeconds;
  const minMinutes = Math.round(effectiveMinSeconds / 60);
  const recordedMinutes = Math.min(Math.floor(timeSpentSeconds / 60), minMinutes);
  const timeMet = effectiveMinSeconds <= 0 || timeSpentSeconds >= effectiveMinSeconds;
  const waitingOnTime = Boolean(result?.passed) && !timeMet;
  const alreadyPassed = Boolean(result?.passed) && timeMet;
  const secondsRemaining = Math.max(0, effectiveMinSeconds - timeSpentSeconds);

  return (
    <Card variant="outlined" sx={{ width: '100%' }}>
      <CardContent>
        <Stack spacing={2}>
          <Typography variant="h5">{pathLabel ?? quiz.title}</Typography>
          <Typography color="text.secondary">Passing score: {quiz.passingScorePercent}%</Typography>
          {effectiveMinSeconds > 0 &&
            (timeMet ? (
              <Typography color="text.secondary">
                Time recorded: {recordedMinutes} of {minMinutes} minute{minMinutes === 1 ? '' : 's'}
              </Typography>
            ) : (
              <QuizMinTimeCountdown secondsRemaining={secondsRemaining} />
            ))}
          {result && (
            <Typography variant="body2" color={result.passed ? 'success.main' : 'warning.main'}>
              Last attempt: {result.scorePercent}% —{' '}
              {result.passed ? (waitingOnTime ? 'Passed, waiting on minimum time' : 'Passed') : 'Not passed'}
            </Typography>
          )}
          {quiz.questions.map((question, index) => (
            <Stack key={question.id} spacing={1}>
              <Typography fontWeight={600}>
                {index + 1}. {question.prompt}
              </Typography>
              <RadioGroup
                value={answers[question.id] ?? ''}
                onChange={(event) => setAnswers({ ...answers, [question.id]: event.target.value })}
              >
                {question.options.map((option) => (
                  <FormControlLabel key={option.id} value={option.id} control={<Radio />} label={option.label} />
                ))}
              </RadioGroup>
            </Stack>
          ))}
          {submit.isError && <Alert severity="error">The quiz could not be submitted.</Alert>}
          <Tooltip title={!timeMet ? SUBMIT_TIME_TOOLTIP : ''}>
            <Box component="span">
              <Button
                ref={submitButtonRef}
                variant="contained"
                onClick={submitQuiz}
                disabled={!complete || submit.isPending || !timeMet || alreadyPassed}
              >
                {alreadyPassed ? 'Passed' : 'Submit quiz'}
              </Button>
            </Box>
          </Tooltip>
        </Stack>
      </CardContent>
      <Popover
        open={scoreOpen && Boolean(result)}
        anchorEl={submitButtonRef.current}
        onClose={() => setScoreOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {result && (
          <Alert severity={result.passed ? (waitingOnTime ? 'info' : 'success') : 'warning'} sx={{ maxWidth: 320 }}>
            Score: {result.scorePercent}% —{' '}
            {result.passed
              ? waitingOnTime
                ? 'Passed. Stay on this quiz until the minimum time is recorded.'
                : 'Passed'
              : 'Not passed. You may try again.'}
          </Alert>
        )}
      </Popover>
    </Card>
  );
}
